<template>
  <div class="grid-section" :class="{ 'is-loading': loading }">
    <div class="grid-toolbar">

      <ul class="grid-nav">
        <li>
          <button
            class="btn btn-icon"
            :class="{ 'nz-hide': !canAdd }"
            @click="addRow"
            :disabled="adding || !canAdd"
          >
            <span class="icon-plus"></span>
          </button>
        </li>
      </ul>

      <input
        v-model="search"
        type="text"
        placeholder="Search..."
        class="global-search"
        @input="triggerSearch"
      />
    </div>

    <div class="ag-theme-quartz grid-wrapper">
      <AgGridVue
        class="w-full h-full"
        style="height: 100%; width: 100%;"
        :context="gridContext"
        :columnDefs="columns"
        :defaultColDef="defaultColDef"
        rowModelType="infinite"
        :pagination="false"
        :cacheBlockSize="pageSize"
        :maxBlocksInCache="5"
        :suppressHorizontalScroll="true"
        @grid-ready="onGridReady"
        @cellValueChanged="onCellValueChanged"
        @filterChanged="onFilterChanged"
        @sortChanged="onSortChanged"
      />
    </div>
  </div>
</template>

<script setup>
/* =======================================================
   Imports
======================================================= */
import { ref, watch, nextTick, onMounted } from "vue";
import { AgGridVue } from "ag-grid-vue3";

import { authFetch } from "@/utils/api.js";
import { useModelStore } from "@/composables/useModelStore.js";
import { gridCellFlash } from "@/utils/gridCellFlash.js";
import ModalManager from "@/components/modal/ModalManager.js";
import ErrorModal from "@/components/modal/ErrorModal.vue";

/* =======================================================
   Stores
======================================================= */
const {
  models,
  modelDef,
  columns,
  currentModel,
  setModel,
  articleGroups,
  articleTypes
} = useModelStore();

/* =======================================================
   Refs / State
======================================================= */
const gridInitialised = ref(false);
const gridApi = ref(null);
const loading = ref(false);
const search = ref("");
const pageSize = ref(15);
const API_KEY = localStorage.getItem("api_key");
const gridContext = {
  onRemove: handleRemove
};
const preventAddingModels = ['orders'];

/* =======================================================
   Default Column Config
======================================================= */
const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  editable: true
};

/* =======================================================
   Initial Model Selection
======================================================= */
onMounted(async () => {
  if (modelDef.value?.path) {
    await setModel(modelDef.value.path);
  } else {
    const firstKey = Object.keys(models)[0];
    if (firstKey) {
      const first = models[firstKey];
      await setModel(first.path ?? firstKey);
    }
  }
});

/* =======================================================
   Debounce Utility
======================================================= */
function useDebounce(delay = 300) {
  let t = null;
  return (fn) => {
    clearTimeout(t);
    t = setTimeout(fn, delay);
  };
}

/* =======================================================
   Pinned row Utility
======================================================= */
function clearPinnedDraftIfPresent() {
  if (!gridApi.value) return;

  const pinnedTopRows = gridApi.value.getPinnedTopRowData?.()
    ?? gridApi.value.getGridOption?.('pinnedTopRowData')
    ?? [];

  if (!Array.isArray(pinnedTopRows) || pinnedTopRows.length === 0) return;

  const hasDraft = pinnedTopRows.some(row => row?.__isNew === true);

  if (hasDraft) {
    gridApi.value.setGridOption('pinnedTopRowData', []);
  }
}

function normalizeNewRow(modelPath, data) {
  switch (modelPath) {
    case 'customers':
      return data.customer ?? data;    
    case 'users':
      return data.user ?? data;
    default:
      return data;
  }
}

const debounceShort = useDebounce(150);
const debounceSearch = useDebounce(350);
const adding = ref(false);
const canAdd = ref(false);

/* =======================================================
   Datasource (AG Grid infinite mode)
======================================================= */
function createDatasource() {
  return {
    getRows: async (params) => {
      if (!gridApi.value) return params.failCallback();

      try {
        const def = modelDef.value;
        if (!def?.path) return params.failCallback();

        loading.value = true;

        // ✅ Infinite Row Model uses top-level startRow/endRow
        const startRow = params.startRow ?? 0;
        const endRow = params.endRow ?? (startRow + pageSize.value);

        const offset = startRow;
        const limit = endRow - startRow;

        const filters = JSON.stringify(gridApi.value.getFilterModel() || {});

        const qs = new URLSearchParams({ offset, limit, filters });
        if (search.value) qs.append("search", search.value);
        // ✅ SERVER-SIDE SORTING
        const sortModel = params.sortModel || [];
        if (sortModel.length > 0) {
          const { colId, sort } = sortModel[0];
          qs.append("sort", colId);
          qs.append("order", sort.toUpperCase());
        }
        const res = await authFetch(`/api/grid/${def.path}?${qs}`, {
          credentials: "include"
        });

        if (!res.ok) return params.failCallback();

        const json = await res.json();
        canAdd.value = preventAddingModels.indexOf(def.path) === -1;

        // ✅ Infinite row model callback
        params.successCallback(json.rows, json.total);
      } catch (err) {
        console.error("❌ Grid fetch error:", err);
        params.failCallback();
      } finally {
        loading.value = false;
      }
    }
  };
}

/* =======================================================
   Grid refresh (DO NOT reset datasource)
======================================================= */
function refreshGrid() {
  if (!gridApi.value || !gridInitialised.value) return;
  gridApi.value.purgeInfiniteCache();
}

/* =======================================================
   Grid Event Handlers
======================================================= */
const onFilterChanged = () => debounceShort(refreshGrid);
const onSortChanged = () => debounceShort(refreshGrid);
const triggerSearch = () => debounceSearch(refreshGrid);

/* =======================================================
   Cell Editing + Flash Animation
======================================================= */
const onCellValueChanged = async ({ data, colDef, newValue, oldValue, node }) => {
  if (newValue === oldValue) return;

  const basePath =
    modelDef.value?.path ||
    (currentModel.value
      ? currentModel.value.replace(/_/g, "-")
      : null);

  if (!basePath) {
    node.setDataValue(colDef.field, oldValue);
    return;
  }

  try {
    const res = await authFetch(`/api/${basePath}/${data.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY
      },
      body: JSON.stringify({ [colDef.field]: newValue })
    });

    if (!res.ok) throw new Error(await res.text());

    requestAnimationFrame(() => {
      gridApi.value &&
        gridCellFlash(gridApi.value, { data, colDef, node });
    });
  } catch (err) {
    node.setDataValue(colDef.field, oldValue);
  }
};

/* =======================================================
   Add Row (Pinned Top for Infinite Grid)
======================================================= */

async function addRow() {
  if (!modelDef.value?.path || !gridApi.value) return;

  adding.value = true;
  const payload = {
    customer_id: localStorage.getItem("customer_id") || 1
  };

  try {
    const res = await authFetch(`/api/${modelDef.value.path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      // Try to extract backend error, fallback to generic
      let message = "Failed to add new record.";
      try {
        const err = await res.json();
        message = err?.message || message;
      } catch {}

      throw new Error(message);
    }

    let newRow = normalizeNewRow(
      modelDef.value.path,
      await res.json()
    );

    // 🔹 Pin newly created row at the top
    gridApi.value.setGridOption("pinnedTopRowData", [
      {
        ...newRow,
        __isNew: true
      }
    ]);

    await nextTick();

    // 🔹 Ensure the pinned row is visible
    gridApi.value.ensureIndexVisible(0);

  } catch (err) {
    console.error("❌ Add row failed:", err);

    ModalManager.open({
      title: "Unable to add record",
      component: ErrorModal,
      size: "sm",
      props: {
        message:
          err.message ||
          "An unexpected error occurred while creating the record."
      }
    });

  } finally {
    adding.value = false;
  }

}

/* =======================================================
   Remove Row
======================================================= */
async function handleRemove(record) {
  if (!modelDef.value?.path || !gridApi.value) return;

  try {
    await authFetch(`/api/${modelDef.value.path}/${record.id}`, {
      method: "DELETE",
      headers: {
        "x-api-key": API_KEY
      }
    });

    // Remove draft overlay if this was it
    clearPinnedDraftIfPresent();

    // Infinite row model → refresh data
    gridApi.value.purgeInfiniteCache();

  } catch (err) {
    console.error("Delete failed:", err);
  }
}

/* =======================================================
   AG Grid Ready
======================================================= */
async function onGridReady(params) {

  if (!params.api) return;

  gridApi.value = params.api;

  // v34+ correct API
  params.api.setGridOption("datasource", createDatasource());

  await nextTick();
  params.api.sizeColumnsToFit();

  // allow events AFTER initialisation
  gridInitialised.value = true;

}

/* =======================================================
   Watchers
======================================================= */
watch(currentModel, async () => {
  search.value = "";
  if (!gridApi.value) return;
  // Clear temporary UI state (draft rows)
  clearPinnedDraftIfPresent();
  canAdd.value = preventAddingModels.indexOf(modelDef.value.path) === -1;

  await nextTick();
  gridApi.value.purgeInfiniteCache();

});

watch(search, triggerSearch);
</script>
